/**
 * 取引一覧(全取引の統合ビュー・要件定義書v2.8 7-0 / モックアップv1.7準拠)。
 * 現行スプレッドシート①の置き換え。システムの入口となる画面で、サイドバー最上部に置く。
 *
 * - 取引日 = 実際にお金が動いた日(CFに反映。DB上は payment_date)
 * - 計上日 = 損益に反映する日(PL。DB上は posting_date)
 * - 会社は列に出さず絞り込み条件のみ(基本はSUMのため)
 * - 権限に応じて閲覧範囲が変わる(全社を見られない人は所属事業部のみ)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { toMonthLabel, toShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchHierarchyNodes, toPathLabel } from '@core/masters/hierarchyRepo';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { deleteDirectEntry, isDirectEntryAccount } from './revenueRepo';
import {
  statusLabelFor,
  EVIDENCE_TYPE_LABELS,
  PAYMENT_METHOD_LABELS,
  type CashAccount,
  type HierarchyNode,
  type Transaction,
} from '@core/lib/types';

function toTx(snap: QueryDocumentSnapshot): Transaction {
  return { id: snap.id, ...(snap.data() as object) } as Transaction;
}

/**
 * 表示用の状態(5段階フロー)。申請以外の paid は「計上済み」(decisions.md 2026-08-21 補足A)。
 * 実績に入るのは 支払い済み/計上済み/完了 のみ。
 */
function displayStatus(t: Transaction): { label: string; cls: string } {
  const label = statusLabelFor(t);
  switch (t.status) {
    case 'paid':
    case 'completed':
      return { label, cls: 'b-ok' };
    case 'approved':
    case 'manager_checked':
      return { label, cls: 'b-pend' };
    case 'rejected':
      return { label, cls: 'b-ng' };
    default:
      return { label, cls: 'b-plan' };
  }
}

function ioOf(t: Transaction): 'in' | 'out' | 'transfer' {
  if (t.type === 'transfer') return 'transfer';
  return t.type === 'income' || t.type === 'cash_in' ? 'in' : 'out';
}

export default function TransactionListPage() {
  const { user, isAdmin, canConfirm, can } = useAuth();
  const {
    accounts,
    departments,
    companies,
    accountName,
    subAccountName,
    departmentName,
  } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<Transaction[]>([]);
  const [nodesByDept, setNodesByDept] = useState<Record<string, HierarchyNode[]>>({});
  const [banksByTx, setBanksByTx] = useState<Record<string, string>>({});
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // 絞り込み
  const [q, setQ] = useState('');
  const [fIo, setFIo] = useState('');
  const [fKind, setFKind] = useState('');
  const [fSrc, setFSrc] = useState('');
  const [fBank, setFBank] = useState('');
  const [fDept, setFDept] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fStatus, setFStatus] = useState('');

  const seesAll = isAdmin || canConfirm || can('company_pl');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const base = collection(getDb(), 'transactions');
      const snap = seesAll
        ? await getDocs(query(base, where('posting_month', '==', month), limit(1000)))
        : await getDocs(
            query(
              base,
              where('department_id', 'in', user.department_ids.slice(0, 30)),
              where('posting_month', '==', month),
              limit(1000),
            ),
          );
      const list = snap.docs.map(toTx).sort((a, b) => (a.payment_date < b.payment_date ? 1 : -1));
      setRows(list);

      // 階層名(出てきた事業部のぶんだけ読む)
      const deptIds = [...new Set(list.map((t) => t.department_id))];
      const nodePairs = await Promise.all(
        deptIds.map(async (d) => [d, await fetchHierarchyNodes(d).catch(() => [])] as const),
      );
      setNodesByDept(Object.fromEntries(nodePairs));

      // 銀行(口座)列: cash_entries を読めるのは経理・管理者のみ
      if (canConfirm) {
        const [accountsList, entriesSnap] = await Promise.all([
          fetchCashAccounts().catch(() => [] as CashAccount[]),
          getDocs(query(collection(getDb(), 'cash_entries'), limit(1000))).catch(() => null),
        ]);
        setCashAccounts(accountsList);
        if (entriesSnap) {
          const nameOf = new Map(accountsList.map((a) => [a.id, a.name]));
          const map: Record<string, string> = {};
          entriesSnap.forEach((d) => {
            const e = d.data() as { transaction_id?: string; cash_account_id?: string; direction?: string };
            if (!e.transaction_id || !e.cash_account_id) return;
            const name = nameOf.get(e.cash_account_id) ?? e.cash_account_id;
            const prev = map[e.transaction_id];
            // 振替は出金側→入金側の2件が紐づくため「A → B」と表示する
            map[e.transaction_id] = prev && !prev.includes(name)
              ? (e.direction === 'in' ? `${prev} → ${name}` : `${name} → ${prev}`)
              : name;
          });
          setBanksByTx(map);
        }
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '取引一覧を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [user, month, seesAll, canConfirm, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const hierLabel = useCallback(
    (t: Transaction): string => {
      if (!t.hierarchy_node_id) return '—';
      const nodes = nodesByDept[t.department_id] ?? [];
      const node = nodes.find((n) => n.id === t.hierarchy_node_id);
      return node ? toPathLabel(node, nodes) : '—';
    },
    [nodesByDept],
  );

  /** セル表示は末端ノード名のみ(フルパスはツールチップで見せる) */
  const hierLeaf = useCallback(
    (t: Transaction): string => {
      if (!t.hierarchy_node_id) return '—';
      const node = (nodesByDept[t.department_id] ?? []).find((n) => n.id === t.hierarchy_node_id);
      return node?.name ?? '—';
    },
    [nodesByDept],
  );

  /** 一覧では短縮名で表示する(列幅を抑えるため。正式名はツールチップ) */
  const deptShort = useCallback(
    (id: string): string => departments.find((d) => d.id === id)?.short_name ?? departmentName(id),
    [departments, departmentName],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((t) => {
      const io = ioOf(t);
      if (fIo && io !== fIo) return false;
      const kind = t.source === 'recurring' ? '固' : '変';
      if (fKind && kind !== fKind) return false;
      const src = t.source === 'manual' ? '手動' : '自動';
      if (fSrc && src !== fSrc) return false;
      if (fDept && t.department_id !== fDept) return false;
      if (fCompany && (t.company_id ?? 'sum') !== fCompany) return false;
      if (fBank && !(banksByTx[t.id] ?? '').includes(fBank)) return false;
      if (fStatus && displayStatus(t).label !== fStatus) return false;
      if (needle) {
        const hay = [
          t.description,
          t.counterparty,
          accountName(t.account_id),
          subAccountName(t.sub_account_id),
          hierLabel(t),
          banksByTx[t.id],
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, fIo, fKind, fSrc, fDept, fCompany, fBank, fStatus, banksByTx, accountName, subAccountName, hierLabel]);

  const sums = useMemo(() => {
    let tin = 0;
    let tout = 0;
    for (const t of filtered) {
      const io = ioOf(t);
      if (io === 'in') tin += t.amount;
      else if (io === 'out') tout += t.amount;
    }
    return { tin, tout };
  }, [filtered]);

  function exportCsv() {
    const head = [
      '入出金', '銀行', '取引日', '計上日', '事業部', '拠点・階層', '支払い先', '摘要',
      '科目①', '科目②', '支払方法', '金額', '備考', '固変', '入力', '状態',
    ];
    const lines = [head.join(',')];
    for (const t of filtered) {
      const io = ioOf(t);
      lines.push(
        [
          io === 'in' ? '入金' : io === 'out' ? '出金' : '振替',
          banksByTx[t.id] ?? '',
          t.payment_date,
          t.posting_date,
          departmentName(t.department_id),
          hierLabel(t).replaceAll(',', ' '),
          t.counterparty ?? '',
          (t.description ?? '').replaceAll(',', ' '),
          accountName(t.account_id),
          subAccountName(t.sub_account_id),
          t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '',
          io === 'out' ? -t.amount : t.amount,
          t.source === 'recurring' ? '繰り返し支払い' : t.evidence_type ? EVIDENCE_TYPE_LABELS[t.evidence_type] : '',
          t.source === 'recurring' ? '固定' : '変動',
          t.source === 'manual' ? '手動' : '自動',
          displayStatus(t).label,
        ].join(','),
      );
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `取引一覧_${month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function remove(t: Transaction) {
    if (!user) return;
    setBusyId(t.id);
    try {
      await deleteDirectEntry(t.id, user.id);
      toast('削除しました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusyId(null);
    }
  }

  /** 行の操作(権限と状態に応じて出し分け。実際の防御はセキュリティルール) */
  function actions(t: Transaction) {
    if (t.source === 'recurring') {
      return isAdmin ? (
        <button className="txn-link" title="繰り返し支払いで管理" onClick={() => navigate('/recurring')}>
          繰り返し支払い
        </button>
      ) : (
        <span className="hint">繰り返し支払い</span>
      );
    }
    const account = accounts.find((a) => a.id === t.account_id);
    // 売上・原価の直接入力: 管理者のみ修正・削除できる(確定後も可)
    if (isDirectEntryAccount(account) && (t.status === 'paid' || t.status === 'completed')) {
      if (!isAdmin) return <span className="hint">—</span>;
      return (
        <>
          <button className="txn-link" onClick={() => navigate('/revenue')}>
            編集
          </button>
          <button
            className="txn-link"
            style={{ color: 'var(--red)' }}
            disabled={busyId === t.id}
            onClick={() => void remove(t)}
          >
            削除
          </button>
        </>
      );
    }
    // 承認フロー中の経費: 申請者本人が申請中・差戻しのときだけ修正できる
    if (t.created_by === user?.id && (t.status === 'applied' || t.status === 'rejected')) {
      return (
        <button className="txn-link" title="経費申請画面で修正できます" onClick={() => navigate('/apply')}>
          修正
        </button>
      );
    }
    // 確定済みの経費は変更不可(4段階フローの不変条件)
    return <span className="hint">—</span>;
  }

  const selectStyle = {
    padding: '8px 10px',
    border: '1px solid var(--line)',
    borderRadius: 6,
    fontSize: 12.5,
    fontFamily: 'inherit',
    background: '#fff',
  } as const;

  return (
    <section className="screen" style={{ maxWidth: 1500 }}>
      <div className="card" style={{ padding: '16px 18px 12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="摘要・取引先・科目で検索"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              flex: 1,
              minWidth: 200,
              padding: '8px 11px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
          <select style={selectStyle} value={fIo} onChange={(e) => setFIo(e.target.value)}>
            <option value="">入出金:すべて</option>
            <option value="in">入金</option>
            <option value="out">出金</option>
            <option value="transfer">振替</option>
          </select>
          <select style={selectStyle} value={fKind} onChange={(e) => setFKind(e.target.value)}>
            <option value="">固定/変動:すべて</option>
            <option value="固">固定</option>
            <option value="変">変動</option>
          </select>
          <select style={selectStyle} value={fSrc} onChange={(e) => setFSrc(e.target.value)}>
            <option value="">入力:すべて</option>
            <option value="自動">自動</option>
            <option value="手動">手動</option>
          </select>
          {canConfirm && (
            <select style={selectStyle} value={fBank} onChange={(e) => setFBank(e.target.value)}>
              <option value="">銀行:すべて</option>
              {cashAccounts.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          <select style={selectStyle} value={fDept} onChange={(e) => setFDept(e.target.value)}>
            <option value="">事業部:すべて</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          {companies.length > 1 && (
            <select style={selectStyle} value={fCompany} onChange={(e) => setFCompany(e.target.value)}>
              <option value="">会社:すべて</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select style={selectStyle} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">状態:すべて</option>
            <option>完了</option>
            <option>支払い済み</option>
            <option>計上済み</option>
            <option>支払い待ち</option>
            <option>承認待ち</option>
            <option>申請中</option>
            <option>差戻し</option>
          </select>
          <button className="btn-line" onClick={exportCsv}>
            CSV出力
          </button>
          <button
            className="btn-green"
            style={{ marginTop: 0, padding: '8px 16px' }}
            onClick={() => navigate('/apply')}
          >
            ＋ 経費申請
          </button>
          <button
            className="btn-line"
            onClick={() => navigate('/revenue')}
          >
            ＋ 売上・原価
          </button>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          <b>{filtered.length}件</b> を表示中({toMonthLabel(month)}・計上月ベース) | 入金合計{' '}
          <b style={{ color: 'var(--green)' }}>{formatYen(sums.tin)}</b> / 出金合計{' '}
          <b style={{ color: 'var(--red)' }}>{formatYen(sums.tout)}</b> / 差引{' '}
          <b>{formatYen(sums.tin - sums.tout)}</b>
        </div>
      </div>

      <div className="card" style={{ padding: '0 18px 16px' }}>
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : (
          <div>
            <table className="tx-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '4.5%' }} />
                <col style={{ width: '4.5%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '4.5%' }} />
                <col style={{ width: '3.5%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7.5%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>入出</th>
                  <th>銀行</th>
                  <th>取引日</th>
                  <th>計上日</th>
                  <th>事業部</th>
                  <th>拠点/階層</th>
                  <th>支払い先</th>
                  <th>摘要</th>
                  <th>科目①/②</th>
                  <th>支払方法</th>
                  <th className="num">金額</th>
                  <th>備考</th>
                  <th>固/変</th>
                  <th>入力</th>
                  <th>状態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="empty">
                      この条件の取引はありません。
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => {
                    const io = ioOf(t);
                    const st = displayStatus(t);
                    return (
                      <tr key={t.id}>
                        <td>
                          {io === 'in' ? (
                            <span className="badge b-plan">入金</span>
                          ) : io === 'out' ? (
                            <span className="badge b-pend">出金</span>
                          ) : (
                            <span className="badge b-ok">振替</span>
                          )}
                        </td>
                        <td title={banksByTx[t.id] ?? ''}>{banksByTx[t.id] ?? '—'}</td>
                        <td>{toShortDate(t.payment_date)}</td>
                        <td>{toShortDate(t.posting_date)}</td>
                        <td title={departmentName(t.department_id)}>{deptShort(t.department_id)}</td>
                        <td className="hint" title={hierLabel(t)}>
                          {hierLeaf(t)}
                        </td>
                        <td title={t.counterparty ?? ''}>{t.counterparty ?? '—'}</td>
                        <td title={t.description ?? ''}>
                          <span className="hint">{t.description ?? ''}</span>
                        </td>
                        <td
                          title={
                            t.type === 'transfer'
                              ? '振替・PL対象外'
                              : `${accountName(t.account_id)} / ${subAccountName(t.sub_account_id)}`
                          }
                        >
                          {t.type === 'transfer' ? (
                            <span className="hint">(振替)</span>
                          ) : (
                            <>
                              {accountName(t.account_id)}
                              <span className="hint"> / {subAccountName(t.sub_account_id)}</span>
                            </>
                          )}
                        </td>
                        <td>
                          <span className="hint">
                            {t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}
                          </span>
                        </td>
                        <td
                          className="num"
                          style={{ fontWeight: 600, color: io === 'in' ? 'var(--green)' : undefined }}
                        >
                          {io === 'in' ? '+' : io === 'out' ? '-' : ''}
                          {formatYen(t.amount)}
                        </td>
                        <td>
                          <span className="hint">
                            {t.source === 'recurring'
                              ? '繰り返し支払い'
                              : t.evidence_type
                                ? EVIDENCE_TYPE_LABELS[t.evidence_type]
                                : t.receipts?.length
                                  ? '領収書'
                                  : '—'}
                          </span>
                        </td>
                        <td>
                          {t.source === 'recurring' ? (
                            <span className="badge b-plan">固定</span>
                          ) : (
                            <span className="badge b-pend">変動</span>
                          )}
                        </td>
                        <td>
                          <span className="hint">{t.source === 'manual' ? '手動' : '自動'}</span>
                        </td>
                        <td>
                          <span className={`badge ${st.cls}`}>{st.label}</span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{actions(t)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          取引日=実際にお金が動いた日(CFに反映)、計上日=損益に反映する日(PL)。
          この一覧がすべての取引の入口です。確定済みの経費は変更できません(売上・原価の直接入力と
          固定費は管理者のみ修正可)。銀行列は入出金と紐づいた取引にのみ表示されます。
        </p>
      </div>
    </section>
  );
}
